import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, CheckBox, PermissionsProvider, Select} from '@/components';
import {Filter} from '@/components/Filter';
import type {SettlementPaymentMethodListParams} from '@/types';
import {AfriexCurrencyCode} from '@/types';

import SettlementAccountUpdateButton from './SettlementAccountUpdateButton';

const currencyOptions = [
  {key: 'All currencies', value: ''},
  ...Object.values(AfriexCurrencyCode).map((currency) => ({
    key: currency,
    value: currency,
  })),
];

type SettlementAccountsFiltersProps = {
  filterCount: number;
  filtersToApply: SettlementPaymentMethodListParams;
  clearFilters: () => void;
  selectAppliedFilters: (
    name: string,
    value: string | boolean | undefined,
  ) => void;
  handleApplyFilters: () => void;
  /** Called after an account is saved so the list refreshes in place. */
  onAccountSaved?: () => void | Promise<void>;
};

export default function SettlementAccountsFilters({
  filterCount,
  filtersToApply,
  clearFilters,
  selectAppliedFilters,
  handleApplyFilters,
  onAccountSaved,
}: SettlementAccountsFiltersProps): React.JSX.Element {
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const handleApplyFilter = (): void => {
    handleApplyFilters();
    setShowFilters(false);
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center space-x-3">
          <Filter
            closeFilter={() => setShowFilters(false)}
            handleShowFilter={() => setShowFilters(!showFilters)}
            label="Filters"
            positionLeft={1}
            bodyClassName="w-full md:w-[480px]"
            data-cy="merchant-settlement-accounts-filter-toggle"
          >
            {showFilters && (
              <div className="p-4 space-y-4 max-w-4xl mx-auto w-full col-span-full">
                <Select
                  label="Currency"
                  dataCy="merchant-settlement-accounts-filter-currency"
                  value={filtersToApply.currencyCode ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    selectAppliedFilters('currencyCode', e.target.value)
                  }
                  options={currencyOptions}
                  className="w-full"
                />

                {/* Archived accounts are hidden by default: they cannot be
                    chosen on a new invoice, so they are not what an operator is
                    usually looking for. */}
                <CheckBox
                  label="Include archived"
                  dataCy="merchant-settlement-accounts-filter-include-archived"
                  checked={Boolean(filtersToApply.includeArchived)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    selectAppliedFilters('includeArchived', e.target.checked)
                  }
                />

                <div className="flex md:justify-end">
                  <Button
                    onClick={clearFilters}
                    colorScheme="cyan"
                    variant="ghost"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={handleApplyFilter}
                    colorScheme="cyan"
                    disabled={filterCount === 0}
                    className="ml-3"
                    dataCy="merchant-settlement-accounts-filter-apply"
                  >
                    Apply Filters {filterCount ? ` (${filterCount})` : ''}
                  </Button>
                </div>
              </div>
            )}
          </Filter>
        </div>

        <PermissionsProvider
          permission={AfriexAdminPermission.SettlementPaymentMethodManage}
        >
          <SettlementAccountUpdateButton onSuccess={onAccountSaved} />
        </PermissionsProvider>
      </div>
    </div>
  );
}
